import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '~/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

import { addFamilySchema, defaultFormValues } from './addFamilySchema';
import type { AddFamilyFormValues } from './addFamilySchema';
import { useCreateFamily } from './useCreateFamily';

interface ParentSectionProps {
  form: any;
  parentType: string;
  label: string;
}

function ParentSection({ form, parentType, label }: ParentSectionProps) {
  const prefix = parentType;

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <h3 className="font-medium">{label}</h3>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`${prefix}.firstName`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="First name" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`${prefix}.lastName`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Last name" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`${prefix}.email`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Email" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`${prefix}.phone`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Phone" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name={`${prefix}.isPrimary`}
        render={({ field }: any) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Primary Parent</FormLabel>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
}

interface ChildSectionProps {
  form: any;
  index: number;
  onRemove: () => void;
}

function ChildSection({ form, index, onRemove }: ChildSectionProps) {
  return (
    <div className="space-y-4 p-4 border rounded-lg relative">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Child {index + 1}</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`children.${index}.firstName`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="First name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`children.${index}.lastName`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Last name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`children.${index}.dateOfBirth`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>Date of Birth</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`children.${index}.gender`}
          render={({ field }: any) => (
            <FormItem>
              <FormLabel>Gender</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="MALE">Male</SelectItem>
                  <SelectItem value="FEMALE">Female</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

interface AddFamilyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string | undefined;
}

export function AddFamilyDialog({ open, onOpenChange, schoolId }: AddFamilyDialogProps) {
  const form = useForm<AddFamilyFormValues>({
    resolver: zodResolver(addFamilySchema),
    defaultValues: defaultFormValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'children',
  });

  const [createFamily, { loading }] = useCreateFamily();

  const onSubmit = async (data: AddFamilyFormValues) => {
    // Build the input, only including parents with data
    const input: any = { schoolId };

    const hasFatherData = data.father?.firstName && data.father?.lastName;
    const hasMotherData = data.mother?.firstName && data.mother?.lastName;

    if (hasFatherData) {
      input.father = {
        firstName: data.father!.firstName,
        lastName: data.father!.lastName,
        email: data.father!.email || null,
        phone: data.father!.phone || null,
        isPrimary: data.father!.isPrimary,
      };
    }

    if (hasMotherData) {
      input.mother = {
        firstName: data.mother!.firstName,
        lastName: data.mother!.lastName,
        email: data.mother!.email || null,
        phone: data.mother!.phone || null,
        isPrimary: data.mother!.isPrimary,
      };
    }

    if (data.children && data.children.length > 0) {
      input.children = data.children.map((child) => ({
        firstName: child.firstName,
        lastName: child.lastName,
        dateOfBirth: child.dateOfBirth || null,
        gender: child.gender,
      }));
    }

    try {
      const result = await createFamily({
        variables: { input },
      });

      if (result.data?.createFamily?.userErrors?.length) {
        const errors = result.data.createFamily.userErrors;
        errors.forEach((error) => {
          form.setError('root', { message: error.message });
        });
        return;
      }

      // Success - close dialog and reset form
      form.reset(defaultFormValues);
      onOpenChange(false);
    } catch (error: any) {
      form.setError('root', { message: error.message });
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset(defaultFormValues);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Family</DialogTitle>
          <DialogDescription>
            Add a new family with parent and children information.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {form.formState.errors.root && (
              <div className="text-sm text-destructive">
                {form.formState.errors.root.message}
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Parents</h3>
              <ParentSection form={form} parentType="father" label="Father" />
              <ParentSection form={form} parentType="mother" label="Mother" />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Children</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ firstName: '', lastName: '', dateOfBirth: '', gender: '' as 'MALE' | 'FEMALE' })}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Child
                </Button>
              </div>

              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No children added yet. Click "Add Child" to add children to this family.
                </p>
              )}

              {fields.map((field, index) => (
                <ChildSection
                  key={field.id}
                  form={form}
                  index={index}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Family'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddFamilyDialog;
